import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Filter, Plus, RefreshCcw, Save, X } from 'lucide-react';
import Field from '@/components/common/Field';
import StickyActions from '@/components/common/StickyActions';
import EmptyState from '@/components/common/EmptyState';
import { can } from '@/security/access.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  createNumeracion,
  getCatalogosNumeraciones,
  listNumeraciones,
  toggleNumeracion,
  updateNumeracion,
} from './numeracionesApi';

const emptyForm = {
  sucursal_id: '',
  tipo_documento: 'GUIA',
  punto_venta_id: '',
  punto_venta_numero: '',
  siguiente_numero: 1,
  longitud_numero: 8,
  relleno_con_ceros: true,
  activo: true,
  observaciones: '',
};

const fieldBaseClass =
  'h-8.5 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.96)] px-3 text-sm text-[rgb(var(--fg))] shadow-[0_1px_3px_rgba(var(--shadow),0.1)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:bg-[rgba(var(--subpanel),0.85)] disabled:text-[rgba(var(--muted),0.9)]';

const panelClassName =
  'rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.95)] shadow-[0_18px_34px_rgba(var(--shadow),0.12)]';

const formatPuntoVentaOption = (pv) => {
  const numero = String(pv.numero ?? '').padStart(4, '0');
  const tipo = pv.tipo_documento ? ` - ${pv.tipo_documento}` : '';
  const detalle = !pv.tipo_documento && pv.descripcion ? ` - ${pv.descripcion}` : '';
  return `PV ${numero}${tipo || detalle}`;
};

const formatTipoDocumento = (tipo) => ({
  AJUSTE_DEBITO: 'Ajuste de débito',
  AJUSTE_CREDITO: 'Ajuste de crédito',
}[tipo] || tipo);

const mapValidationErrors = (message = '') => {
  const errors = {};
  if (message.includes('sucursal_id')) errors.sucursal_id = message;
  if (message.includes('tipo_documento')) errors.tipo_documento = message;
  if (message.includes('punto_venta') || message.includes('punto de venta')) errors.punto_venta_id = message;
  if (message.includes('siguiente_numero')) errors.siguiente_numero = message;
  if (message.includes('longitud_numero')) errors.longitud_numero = message;
  if (message.includes('Ya existe una numeracion')) errors.tipo_documento = message;
  return errors;
};

export default function NumeracionesPage() {
  const { user } = useAuth();
  const canAdminister = can(user, 'NUMERACIONES_ADMINISTRAR');
  const [catalogos, setCatalogos] = useState({ tipos_documento: [], sucursales: [], puntos_venta: [] });
  const [filtros, setFiltros] = useState({ sucursal_id: '', tipo_documento: '' });
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === 'undefined' ? 1440 : window.innerWidth,
    height: typeof window === 'undefined' ? 900 : window.innerHeight,
  }));
  const [formOpen, setFormOpen] = useState(() => (
    typeof window === 'undefined'
      ? true
      : !(window.innerWidth <= 1366 || window.innerHeight < 720)
  ));

  const isLowHeight = viewport.height < 720;
  const isVeryLowHeight = viewport.height < 680;
  const isCompactOperative = viewport.width < 1200;
  const isEditing = Boolean(editingId);

  const loadCatalogos = useCallback(async (sucursalForCatalogo = '') => {
    const catalogoData = await getCatalogosNumeraciones(sucursalForCatalogo || undefined);
    setCatalogos(catalogoData);
  }, []);

  const loadNumeraciones = useCallback(async (filters = filtros) => {
    setIsLoadingTable(true);
    try {
      const numeracionesData = await listNumeraciones(filters);
      setRows(numeracionesData);
    } finally {
      setIsLoadingTable(false);
    }
  }, [filtros]);

  const loadInitialData = useCallback(async () => {
    try {
      setError('');
      await Promise.all([loadCatalogos(), loadNumeraciones()]);
    } catch (e) {
      setError(e.message);
    }
  }, [loadCatalogos, loadNumeraciones]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const puntosVentaFiltrados = useMemo(
    () => catalogos.puntos_venta.filter((pv) => (
      String(pv.sucursal_id) === String(form.sucursal_id)
      && (!form.tipo_documento || String(pv.tipo_documento || '') === String(form.tipo_documento))
    )),
    [catalogos.puntos_venta, form.sucursal_id, form.tipo_documento],
  );

  const creatingPuntoVenta = form.punto_venta_id === '__nuevo__';

  useEffect(() => {
    if (!form.punto_venta_id || creatingPuntoVenta) return;
    const selectedStillValid = puntosVentaFiltrados.some((pv) => String(pv.id) === String(form.punto_venta_id));
    if (!selectedStillValid) {
      setForm((prev) => ({ ...prev, punto_venta_id: '' }));
    }
  }, [creatingPuntoVenta, form.punto_venta_id, puntosVentaFiltrados]);

  const formattedRows = useMemo(
    () => rows.map((row) => ({
      ...row,
      numero_formateado: row.relleno_con_ceros
        ? String(row.siguiente_numero).padStart(row.longitud_numero, '0')
        : String(row.siguiente_numero),
    })),
    [rows],
  );

  const resetFormState = () => {
    setForm(emptyForm);
    setEditingId(null);
    setFieldErrors({});
    setError('');
  };

  const openCreateForm = async () => {
    resetFormState();
    setFormOpen(true);
    try {
      await loadCatalogos();
    } catch (e) {
      setError(e.message);
    }
  };

  const closeForm = () => {
    resetFormState();
    setFormOpen(false);
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.sucursal_id) nextErrors.sucursal_id = 'Selecciona una sucursal.';
    if (!form.tipo_documento) nextErrors.tipo_documento = 'Selecciona un tipo de documento.';
    if (!form.punto_venta_id) nextErrors.punto_venta_id = 'Elegí o creá un punto de venta.';
    if (creatingPuntoVenta && (!Number.isFinite(Number(form.punto_venta_numero)) || Number(form.punto_venta_numero) <= 0)) {
      nextErrors.punto_venta_id = 'Indicá un número de punto de venta mayor a 0.';
    }

    const siguienteNumero = Number(form.siguiente_numero);
    if (!Number.isFinite(siguienteNumero) || siguienteNumero <= 0) {
      nextErrors.siguiente_numero = 'El proximo numero debe ser mayor a 0.';
    }

    const longitudNumero = Number(form.longitud_numero);
    if (!Number.isFinite(longitudNumero) || longitudNumero <= 0) {
      nextErrors.longitud_numero = 'La longitud debe ser mayor a 0.';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onChangeSucursalForm = async (sucursalId) => {
    const previousPuntoVenta = form.punto_venta_id;
    setForm((prev) => ({ ...prev, sucursal_id: sucursalId, punto_venta_id: '', punto_venta_numero: '' }));
    setFieldErrors((prev) => ({ ...prev, sucursal_id: undefined, punto_venta_id: undefined }));

    try {
      const catalogoData = await getCatalogosNumeraciones(sucursalId || undefined);
      setCatalogos(catalogoData);
      const puntosDeSucursal = catalogoData.puntos_venta.filter((pv) => (
        String(pv.sucursal_id) === String(sucursalId)
        && (!form.tipo_documento || String(pv.tipo_documento || '') === String(form.tipo_documento))
      ));
      const selectedStillValid = puntosDeSucursal.some((pv) => String(pv.id) === String(previousPuntoVenta));
      if (selectedStillValid) {
        setForm((prev) => ({ ...prev, punto_venta_id: previousPuntoVenta }));
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      setError('Revisa los campos marcados para continuar.');
      return;
    }

    try {
      setIsSaving(true);
      setError('');
      const { punto_venta_id: selectedPuntoVentaId, punto_venta_numero: nuevoPuntoVentaNumero, ...formValues } = form;
      const payload = {
        ...formValues,
        sucursal_id: Number(form.sucursal_id),
        siguiente_numero: Number(form.siguiente_numero),
        longitud_numero: Number(form.longitud_numero),
        ...(creatingPuntoVenta
          ? {
            crear_punto_venta: true,
            punto_venta_numero: Number(nuevoPuntoVentaNumero),
          }
          : { punto_venta_id: Number(selectedPuntoVentaId) }),
      };

      if (editingId) {
        await updateNumeracion(editingId, payload);
      } else {
        await createNumeracion(payload);
      }

      await Swal.fire({
        icon: 'success',
        title: editingId ? 'Numeracion actualizada' : 'Numeracion creada',
        text: 'La configuracion quedo guardada correctamente.',
        confirmButtonText: 'Continuar',
      });

      closeForm();
      await Promise.all([loadCatalogos(), loadNumeraciones(filtros)]);
    } catch (e) {
      const nextErrors = mapValidationErrors(e.message);
      setFieldErrors((prev) => ({ ...prev, ...nextErrors }));
      setError(e.message);
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo guardar la numeracion',
        text: e.message,
        confirmButtonText: 'Entendido',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async (row) => {
    setError('');
    setFieldErrors({});
    setEditingId(row.id);
    setFormOpen(true);

    try {
      const catalogoData = await getCatalogosNumeraciones(row.sucursal_id);
      setCatalogos(catalogoData);
      setForm({
        sucursal_id: String(row.sucursal_id),
        tipo_documento: row.tipo_documento,
        punto_venta_id: String(row.punto_venta_id),
        siguiente_numero: row.siguiente_numero,
        longitud_numero: row.longitud_numero,
        relleno_con_ceros: Boolean(row.relleno_con_ceros),
        activo: Boolean(row.activo),
        observaciones: row.observaciones || '',
      });
    } catch (e) {
      setError(e.message);
    }
  };

  const handleToggle = async (row) => {
    try {
      await toggleNumeracion(row.id, !row.activo);
      await loadNumeraciones(filtros);
    } catch (e) {
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo actualizar el estado',
        text: e.message,
        confirmButtonText: 'Cerrar',
      });
    }
  };

  const handleApplyFilters = async () => {
    try {
      setError('');
      await loadNumeraciones(filtros);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      const saveShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's';
      if (!saveShortcut || isSaving || !formOpen) return;
      event.preventDefault();
      handleSubmit();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [formOpen, isSaving]);

  return (
    <div className={`mx-auto w-full max-w-[1400px] space-y-2 ${isLowHeight ? 'px-2 pb-3 pt-2 md:px-3' : 'px-3 pb-5 pt-3 md:px-4'}`}>
      <header className={`${panelClassName} ${isLowHeight ? 'px-3 py-2' : 'px-4 py-3'}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-[rgba(var(--muted),0.9)]">
              <Link to="/administracion" className="rounded-lg px-1.5 py-0.5 hover:text-[rgb(var(--brand))]">Administracion</Link>
              <span>/</span>
              <Link to="/administracion/empresa" className="rounded-lg px-1.5 py-0.5 hover:text-[rgb(var(--brand))]">Empresa</Link>
              <span>/</span>
              <span className="text-[rgb(var(--fg))]">Numeracion</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <h1 className="text-base font-semibold tracking-tight text-[rgb(var(--fg))] md:text-[1.1rem]">Numeraciones por sucursal</h1>
              <span className="rounded-full border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.82)] px-2 py-0.5 text-[11px] font-medium text-[rgb(var(--fg))]">
                {formattedRows.length} registros
              </span>
            </div>
          </div>
          <button
            type="button"
            className="inline-flex h-8.5 items-center gap-2 rounded-xl border border-[rgba(var(--border),0.75)] bg-[rgba(var(--panel),0.9)] px-3 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            onClick={loadInitialData}
          >
            <RefreshCcw className="h-4 w-4" />
            Recargar
          </button>
        </div>
      </header>

      {error ? (
        <section className="rounded-2xl border border-[rgba(var(--brand-700),0.35)] bg-[rgba(var(--brand),0.1)] px-3 py-2 text-sm text-[rgb(var(--fg))]" role="alert">
          <p className="font-semibold">Hay validaciones pendientes</p>
          <p className="mt-0.5 text-[rgba(var(--fg),0.9)]">{error}</p>
        </section>
      ) : null}

      <section className={`${panelClassName} ${isLowHeight ? 'p-3' : 'p-4'}`}>
        <div className="mb-2 flex items-center gap-2">
          <Filter className="h-4 w-4 text-[rgb(var(--brand))]" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">Filtros</h2>
        </div>

        <div className={`grid grid-cols-12 ${isLowHeight ? 'gap-2' : 'gap-3'}`}>
          <div className="col-span-12 md:col-span-4">
            <Field label="Sucursal" htmlFor="filtro-sucursal">
              <select
                id="filtro-sucursal"
                className={fieldBaseClass}
                value={filtros.sucursal_id}
                onChange={(e) => setFiltros((prev) => ({ ...prev, sucursal_id: e.target.value }))}
              >
                <option value="">Todas las sucursales</option>
                {catalogos.sucursales.map((sucursal) => (
                  <option key={sucursal.id} value={sucursal.id}>{sucursal.nombre}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="col-span-12 md:col-span-4">
            <Field label="Tipo de documento" htmlFor="filtro-tipo">
              <select
                id="filtro-tipo"
                className={fieldBaseClass}
                value={filtros.tipo_documento}
                onChange={(e) => setFiltros((prev) => ({ ...prev, tipo_documento: e.target.value }))}
              >
                <option value="">Todos los tipos</option>
                {catalogos.tipos_documento.map((tipo) => (
                  <option key={tipo} value={tipo}>{formatTipoDocumento(tipo)}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="col-span-12 flex items-end md:col-span-4">
            <button
              type="button"
              className="inline-flex h-8.5 w-full items-center justify-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-3 text-sm font-semibold text-[rgb(var(--panel))] transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              onClick={handleApplyFilters}
            >
              Aplicar filtros
            </button>
          </div>
        </div>
      </section>

      <section className={`${panelClassName} ${isLowHeight ? 'p-3' : 'p-4'}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {canAdminister && (
              <button
                type="button"
                className="inline-flex h-8.5 items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-3 text-sm font-semibold text-[rgb(var(--panel))] transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                onClick={openCreateForm}
              >
                <Plus className="h-4 w-4" />
                Nueva numeracion
              </button>
            )}
            <span className="rounded-full border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.82)] px-2 py-0.5 text-[11px] font-medium text-[rgb(var(--fg))]">
              {formattedRows.length} visibles
            </span>
          </div>

        </div>
      </section>

      {formOpen ? (
        <form onSubmit={handleSubmit} className={`${panelClassName} ${isLowHeight ? 'p-3' : 'p-4'}`}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-[rgb(var(--brand))]" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">{isEditing ? 'Editar numeracion' : 'Nueva numeracion'}</h2>
            </div>
            <button
              type="button"
              className="inline-flex h-8 items-center gap-1 rounded-xl border border-[rgba(var(--border),0.7)] px-3 text-xs font-semibold text-[rgb(var(--fg))]"
              onClick={closeForm}
            >
              <X className="h-3.5 w-3.5" />
              Cerrar
            </button>
          </div>

          <div className={`grid grid-cols-12 ${isLowHeight ? 'gap-2' : 'gap-3'}`}>
            <div className="col-span-12 md:col-span-4">
              <Field label="Sucursal" htmlFor="form-sucursal" required error={fieldErrors.sucursal_id}>
                <select
                  id="form-sucursal"
                  className={fieldBaseClass}
                  value={form.sucursal_id}
                  onChange={(e) => onChangeSucursalForm(e.target.value)}
                  required
                >
                  <option value="">Selecciona una sucursal</option>
                  {catalogos.sucursales.map((sucursal) => (
                    <option key={sucursal.id} value={sucursal.id}>{sucursal.nombre}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="col-span-12 md:col-span-4">
              <Field label="Tipo" htmlFor="form-tipo" required error={fieldErrors.tipo_documento}>
                <select
                  id="form-tipo"
                  className={fieldBaseClass}
                  value={form.tipo_documento}
                  onChange={(e) => {
                    const nextTipo = e.target.value;
                    setForm((prev) => ({ ...prev, tipo_documento: nextTipo, punto_venta_id: '', punto_venta_numero: '' }));
                    setFieldErrors((prev) => ({ ...prev, tipo_documento: undefined, punto_venta_id: undefined }));
                  }}
                  required
                >
                  {catalogos.tipos_documento.map((tipo) => (
                    <option key={tipo} value={tipo}>{formatTipoDocumento(tipo)}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="col-span-12 md:col-span-4">
              <Field label="Punto de venta" htmlFor="form-punto-venta" required error={fieldErrors.punto_venta_id}>
                <select
                  id="form-punto-venta"
                  className={fieldBaseClass}
                  value={form.punto_venta_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, punto_venta_id: e.target.value }))}
                  required
                  disabled={!form.sucursal_id}
                >
                  <option value="">
                    {!form.sucursal_id
                      ? 'Selecciona una sucursal primero'
                      : 'Selecciona un punto de venta'}
                  </option>
                  {!isEditing ? <option value="__nuevo__">Crear nuevo punto de venta…</option> : null}
                  {puntosVentaFiltrados.map((puntoVenta) => (
                    <option key={puntoVenta.id} value={puntoVenta.id}>{formatPuntoVentaOption(puntoVenta)}</option>
                  ))}
                </select>
              </Field>
            </div>

            {creatingPuntoVenta ? (
              <div className="col-span-12 md:col-span-4">
                <Field label="Número del nuevo punto de venta" htmlFor="form-punto-venta-numero" required error={fieldErrors.punto_venta_id}>
                  <input
                    id="form-punto-venta-numero"
                    required
                    min="1"
                    type="number"
                    className={fieldBaseClass}
                    placeholder="Ej. 1"
                    value={form.punto_venta_numero}
                    onChange={(e) => setForm((prev) => ({ ...prev, punto_venta_numero: e.target.value }))}
                  />
                </Field>
              </div>
            ) : null}

            <div className="col-span-12 md:col-span-3">
              <Field label="Proximo" htmlFor="form-siguiente" required error={fieldErrors.siguiente_numero}>
                <input
                  id="form-siguiente"
                  required
                  min="1"
                  type="number"
                  className={fieldBaseClass}
                  value={form.siguiente_numero}
                  onChange={(e) => setForm((prev) => ({ ...prev, siguiente_numero: e.target.value }))}
                />
              </Field>
            </div>

            <div className="col-span-12 md:col-span-3">
              <Field label="Longitud" htmlFor="form-longitud" required error={fieldErrors.longitud_numero}>
                <input
                  id="form-longitud"
                  required
                  min="1"
                  type="number"
                  className={fieldBaseClass}
                  value={form.longitud_numero}
                  onChange={(e) => setForm((prev) => ({ ...prev, longitud_numero: e.target.value }))}
                />
              </Field>
            </div>

            <div className="col-span-12 md:col-span-3">
              <Field label="Observaciones" htmlFor="form-observaciones">
                <input
                  id="form-observaciones"
                  className={fieldBaseClass}
                  placeholder="Notas internas"
                  value={form.observaciones}
                  onChange={(e) => setForm((prev) => ({ ...prev, observaciones: e.target.value }))}
                />
              </Field>
            </div>

            <div className="col-span-12 md:col-span-4 flex items-end">
              <label className="flex min-h-[2.125rem] items-center gap-2 text-sm text-[rgb(var(--fg))]">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border border-[rgba(var(--border),0.7)] text-[rgb(var(--brand))] focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  checked={form.relleno_con_ceros}
                  onChange={(e) => setForm((prev) => ({ ...prev, relleno_con_ceros: e.target.checked }))}
                />
                Completar con ceros
              </label>
            </div>

            <div className="col-span-12 md:col-span-4 flex items-end">
              <label className="flex min-h-[2.125rem] items-center gap-2 text-sm text-[rgb(var(--fg))]">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border border-[rgba(var(--border),0.7)] text-[rgb(var(--brand))] focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  checked={form.activo}
                  onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
                />
                Numeracion activa
              </label>
            </div>
          </div>

          {creatingPuntoVenta ? (
            <div className="mt-3 rounded-xl border border-[rgba(var(--brand-700),0.35)] bg-[rgba(var(--brand),0.1)] px-3 py-2 text-sm text-[rgb(var(--fg))]">
              <p className="font-semibold">Se creará el punto de venta junto con esta numeración.</p>
              <p className="mt-1">Indicá el número del PV y el próximo número de documento; no necesitás ir a otra pantalla.</p>
            </div>
          ) : null}

          <StickyActions
            onCancel={closeForm}
            onSubmit={handleSubmit}
            isSubmitting={isSaving}
            primaryLabel={isEditing ? 'Guardar cambios' : 'Crear numeracion'}
            secondaryLabel={isEditing ? 'Cancelar edicion' : 'Cerrar'}
            disablePrimary={!form.sucursal_id || !form.punto_venta_id || (creatingPuntoVenta && !form.punto_venta_numero)}
          />
        </form>
      ) : null}

      <section className={`${panelClassName} ${isLowHeight ? 'p-3' : 'p-4'}`}>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">Listado de numeraciones</h2>
          <span className="rounded-full border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.82)] px-2 py-0.5 text-[11px] font-medium text-[rgb(var(--fg))]">
            {formattedRows.length} visibles
          </span>
        </div>

        {isLoadingTable ? (
          <div className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.8)] px-4 py-5 text-sm text-[rgba(var(--muted),0.95)]">
            Cargando numeraciones...
          </div>
        ) : formattedRows.length === 0 ? (
          <EmptyState
            title="No hay numeraciones cargadas"
            description="Crea la primera numeracion para verla aca."
          />
        ) : (
          <div className={`${isVeryLowHeight ? 'max-h-[36vh]' : isLowHeight ? 'max-h-[42vh]' : 'max-h-[56vh]'} overflow-auto rounded-xl border border-[rgba(var(--border),0.6)]`}>
            <table className="min-w-full text-sm">
              <thead className="bg-[rgba(var(--subpanel),0.95)] text-[rgba(var(--muted),0.95)]">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold">Sucursal</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Tipo</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Punto de venta</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Proximo</th>
                  {!isCompactOperative ? <th className="px-3 py-2.5 text-left font-semibold">Long.</th> : null}
                  <th className="px-3 py-2.5 text-left font-semibold">Estado</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {formattedRows.map((row) => (
                  <tr key={row.id} className="border-t border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.7)]">
                    <td className="px-3 py-2.5 text-[rgb(var(--fg))]">{row.sucursal_nombre}</td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex rounded-full border border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.14)] px-2.5 py-1 text-xs font-semibold text-[rgb(var(--fg))]">
                        {row.tipo_documento}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[rgb(var(--fg))]">
                      PV {String(row.punto_venta_numero || '').padStart(4, '0')}
                      {!isCompactOperative && (row.punto_venta_tipo_documento || row.punto_venta_descripcion)
                        ? ` - ${row.punto_venta_tipo_documento || row.punto_venta_descripcion}`
                        : ''}
                    </td>
                    <td className="px-3 py-2.5 text-[rgb(var(--fg))]">{row.numero_formateado}</td>
                    {!isCompactOperative ? <td className="px-3 py-2.5 text-[rgb(var(--fg))]">{row.longitud_numero}</td> : null}
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${row.activo ? 'bg-[rgba(var(--brand),0.2)] text-[rgb(var(--fg))]' : 'bg-[rgba(var(--border),0.4)] text-[rgba(var(--muted),0.95)]'}`}>
                        {row.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      {canAdminister && (
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="rounded-xl border border-[rgba(var(--border),0.75)] px-2.5 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          onClick={() => handleEdit(row)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="rounded-xl bg-[rgba(var(--brand),0.2)] px-2.5 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--brand),0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          onClick={() => handleToggle(row)}
                        >
                          {row.activo ? 'Desactivar' : 'Activar'}
                        </button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="flex items-center gap-2 text-xs text-[rgba(var(--muted),0.92)]">
        <Save className="h-3.5 w-3.5" aria-hidden="true" />
        Atajo: usa Ctrl+S o Cmd+S para guardar cuando el panel esta abierto.
      </p>
    </div>
  );
}






